import { EventType, PublicClientApplication } from '@azure/msal-browser';
import type { AuthenticationResult, EventMessage } from '@azure/msal-browser';
import type { AppConfig } from '@/config/appConfig';
import { buildMsalConfig } from './msalConfig';

/**
 * Creates and initialises a PublicClientApplication instance.
 *
 * - Uses new PublicClientApplication(config) + initialize() (msal-browser v5 pattern).
 * - Calls handleRedirectPromise() BEFORE React mounts so that when the app loads
 *   after a Microsoft login redirect, the account is already in the MSAL cache.
 *   Without this, React's first render sees isAuthenticated=false (redirect not yet
 *   processed), shows the sign-in page, and creates a redirect loop.
 * - Sets the active account from the existing cache on page load so that
 *   acquireTokenSilent works immediately after a refresh.
 * - Registers a LOGIN_SUCCESS event handler to set the active account after
 *   a redirect-based login completes.
 */
export async function createMsalInstance(config: AppConfig): Promise<PublicClientApplication> {
    const instance = new PublicClientApplication(buildMsalConfig(config));
    await instance.initialize();

    // Process the redirect response (auth code in URL) before React mounts.
    // This ensures the account is in the MSAL cache on the very first render,
    // preventing the sign-in page from flashing or looping after login.
    try {
        await instance.handleRedirectPromise();
    } catch (e) {
        // Redirect errors (e.g. user cancelled, config mismatch) are non-fatal here —
        // they will surface through the normal MSAL event/error flow once React mounts.
        console.error('[Auth] handleRedirectPromise error during bootstrap:', e);
    }

    // Restore active account from the cache on page load / refresh.
    // Must run after handleRedirectPromise() so newly-returned accounts are included.
    const cachedAccounts = instance.getAllAccounts();
    if (cachedAccounts.length > 0) {
        instance.setActiveAccount(cachedAccounts[0]);
    }

    // After a successful login redirect, set the returned account as active
    instance.addEventCallback((event: EventMessage) => {
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
            const payload = event.payload as AuthenticationResult;
            instance.setActiveAccount(payload.account);
        }
    });

    return instance;
}
